import { useCanCreateAmazonMarketplaceDeal } from '@openx/components-deal/lib/components/DealParticipants/hooks/useCanCreateAmazonMarketplaceDeal.ts';
import { dealThirdPartyFeesConfigDefaultValues } from '@openx/components-deal/lib/DealForm/utils/constants.ts';
import {
  getInitialDealFormValues,
  getUseAsTemplateDeal,
} from '@openx/components-deal/lib/DealForm/utils/dealFormUtils.ts';
import { parseDealDate } from '@openx/components-deal/lib/DealForm/utils/parseDealDate.ts';
import { parseThirdPartyFeesConfig } from '@openx/components-deal/lib/DealForm/utils/parseThirdPartyFeesConfig.ts';
import { type DealProviderConfig, useDealContext } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import { getInitialPckValues } from '@openx/components-package/components/PackageForm/constants.ts';
import { packageToFormState } from '@openx/components-package/components/PackageForm/utils/formStateConverter.ts';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import {
  type DealApi,
  type DealFormData,
  type DealFormDataWithTargeting,
  type DealState,
  dealFormField,
  pmpDealType,
} from '@openx/types/deal.ts';
import { Feature_Flags_Enum } from '@openx/types/gqlTypes.ts';
import type { OrganizationType } from '@openx/types/organization.ts';
import type { PackageFormState } from '@openx/types/package.ts';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes.ts';
import { cleanUpNulls } from '@openx/utils/lib/cleanUpNulls.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { DV360_DEMAND_PARTNER_ID, OPEN_AUDIENCE_ORG_NAME } from '@openx/utils/services/config.ts';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from '../../../components/AuthContext/context.ts';
import { useFeatureFlag } from '../../../modules/featureFlags/useFeatureFlag.ts';
import { isInternalUser } from '../../../permissions/validators.ts';

const getAudienceStructure = (audienceId: string) => ({
  targeting: {
    audience: {
      openaudience_custom: {
        op: Intersect.INTERSECTS,
        val: `openaudience-${audienceId}`,
      },
    },
  },
});

export const useInitialValues: DealProviderConfig['useInitialValues'] = (
  dealForm,
  defaultAccountUid,
  pckg,
  enforcedUseAs
) => {
  const { session } = useAuth();
  const { onBehalfOrganization } = useDealContext();
  const hasCuratedDealBuyerDiscountFeature = useFeatureFlag(Feature_Flags_Enum.CuratedDealBuyerDiscount);
  const locationState = useLocation().state;
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();
  const canCreateAmazonMarketplaceDeal = useCanCreateAmazonMarketplaceDeal();

  const accountUid = locationState?.accountUid || defaultAccountUid;

  const getPackageFormData = useCallback((): PackageFormState => {
    if (pckg && !locationState?.useUniqueTargeting) {
      return packageToFormState(pckg);
    }
    return getInitialPckValues(
      accountUid || undefined,
      locationState?.audienceId && getAudienceStructure(locationState?.audienceId),
      locationState?.dsInitialDealFormPreset
    );
  }, [
    accountUid,
    locationState?.audienceId,
    locationState?.dsInitialDealFormPreset,
    locationState?.useUniqueTargeting,
    pckg,
  ]);

  const isEdit = !!dealForm.currentDeal;
  const isUseAsTemplate = enforcedUseAs ? enforcedUseAs === 'template' : !!locationState?.useAsTemplate;
  const isUseSharedTargeting = enforcedUseAs ? enforcedUseAs === 'targeting' : !!locationState?.useSharedTargeting;

  const getDealFormData = useCallback((): DealFormData => {
    const deal: DealState | undefined = cloneDeep(dealForm.currentDeal);
    const organizationType = (onBehalfOrganization?.organization_type || session?.account?.organization_type) as
      | OrganizationType
      | undefined;

    const initialDealFormState = getInitialDealFormValues({
      defaultAccountUid: accountUid,
      isOa: true,
      organizationType,
      packageUid: locationState?.packageUid,
      useSharedTargeting: locationState?.useSharedTargeting,
    });

    const templateDeal = cleanUpNulls(
      getUseAsTemplateDeal(
        (deal as unknown as DealApi) ?? {},
        {
          dv360DemandPartnerId: DV360_DEMAND_PARTNER_ID,
        },
        canCreateAmazonMarketplaceDeal
      )
    );

    let dealFormData: DealFormData | null;

    const dealState = deal ?? initialDealFormState;

    const parsedEndDate = parseDealDate(dealState.end_date);
    const parsedStartDate = parseDealDate(dealState.start_date);

    const skipPlatformShareParsing =
      (isUseAsTemplate || isUseSharedTargeting) &&
      !isAllowed(isAllowedByCompiledAcl(ACL_TYPE.THIRD_PARTY_FEES_PLATFORM_SHARE_WRITE));

    const thirdPartyFeesConfigEnabled =
      (isEdit || isUseAsTemplate || isUseSharedTargeting) && !!deal?.third_party_fees_config;

    const thirdPartyFeesConfig = thirdPartyFeesConfigEnabled
      ? parseThirdPartyFeesConfig(dealForm.currentDeal?.third_party_fees_config, skipPlatformShareParsing)
      : [dealThirdPartyFeesConfigDefaultValues];

    if (isUseAsTemplate || isUseSharedTargeting) {
      dealFormData = {
        ...initialDealFormState,
        ...templateDeal,
        end_date: parsedEndDate,
        start_date: parsedStartDate,
        third_party_fees_config: thirdPartyFeesConfig,
        third_party_fees_config_enabled: thirdPartyFeesConfigEnabled,
        ...(dealState.monetizing_uid ? { monetizing_uid: dealState.monetizing_uid } : {}),
      };

      if (dealFormData && !isInternalUser(session)) {
        dealFormData.agency_salesforce_id = undefined;
        dealFormData.brand_salesforce_id = undefined;
      }
    } else {
      dealFormData = {
        ...dealState,
        end_date: parsedEndDate,
        start_date: parsedStartDate,
        third_party_fees_config: thirdPartyFeesConfig,
        third_party_fees_config_enabled: thirdPartyFeesConfigEnabled,
      };
    }

    if (!dealFormData) {
      throw new Error('dealFormData must be defined.');
    }

    dealFormData.deal_participants = dealFormData.deal_participants?.map(participant => ({
      ...participant,
      brand_ids: participant.brand_ids ?? [],
      buyer_id: participant.buyer_id ?? null,
      buyer_ids: participant.buyer_ids ?? [],
      main_buyer_id: participant.main_buyer_id ?? null,
    }));

    if (!dealFormData.pmp_deal_type) dealFormData.pmp_deal_type = pmpDealType.WithinOpenAuction;

    if (!dealFormData.currency) dealFormData.currency = 'USD';
    if (!dealFormData.expected_ad_category) dealFormData.expected_ad_category = null;

    if (!dealFormData.deal_source && session?.account?.name) {
      dealFormData.deal_source =
        session?.account?.name === OPEN_AUDIENCE_ORG_NAME ? 'OpenX - Buyer Dev Sourced' : 'Tech Partner Sourced';
    }

    if (hasCuratedDealBuyerDiscountFeature) {
      if (dealFormData[dealFormField.IS_DISCOUNT_ENABLED] === undefined) {
        dealFormData[dealFormField.IS_DISCOUNT_ENABLED] = true;
      }
    } else {
      delete dealFormData[dealFormField.IS_DISCOUNT_ENABLED];
    }

    if (dealFormData.account_executive === undefined) {
      dealFormData.account_executive = '';
    }

    return dealFormData;
  }, [
    accountUid,
    canCreateAmazonMarketplaceDeal,
    dealForm.currentDeal,
    hasCuratedDealBuyerDiscountFeature,
    isAllowed,
    isEdit,
    isUseAsTemplate,
    isUseSharedTargeting,
    locationState?.packageUid,
    locationState?.useSharedTargeting,
    onBehalfOrganization?.organization_type,
    session,
  ]);

  const package_uid =
    (!isUseAsTemplate && !locationState?.useUniqueTargeting && (locationState?.packageUid as string)) ||
    (!isUseAsTemplate && !locationState?.useUniqueTargeting && dealForm.currentDeal?.package_uid) ||
    null;

  const initialValues: DealFormDataWithTargeting = useMemo(() => {
    return structuredClone({
      ...getDealFormData(),
      targeting: getPackageFormData(),
      package_uid,
      __meta: {
        group_deal: null,
      },
    });
  }, [package_uid, getDealFormData, getPackageFormData]);

  return initialValues;
};

export const useInitialValuesLazy: DealProviderConfig['useInitialValuesLazy'] = (
  dealForm,
  defaultAccountUid,
  pckg,
  enforcedUseAs
) => {
  const initialValues = useInitialValues(dealForm, defaultAccountUid, pckg || undefined, enforcedUseAs);
  return () => initialValues;
};
